import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'

export function Login() {
  const { isAuthenticated, isLoading, refetch } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const error = searchParams.get('error')

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Check for successful callback
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      refetch()
    }
  }, [searchParams, refetch])

  const handleSlackLogin = () => {
    window.location.href = '/api/auth/slack'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        {/* Logo and title */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-brand-teal rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">GoalsDash</h1>
          <p className="mt-2 text-gray-600">Track your team's KPIs and goals</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 text-danger-600 text-sm">
            {error === 'access_denied'
              ? 'You denied access to your Slack account.'
              : error === 'unauthorized_workspace'
                ? 'Your Slack account is not in the workspace allowed to use GoalsDash.'
                : error === 'workspace_mismatch'
                  ? 'This account is linked to a different Slack workspace. Contact an administrator.'
                  : error === 'auth_not_configured'
                    ? 'Sign-in is not configured right now. Contact an administrator.'
                    : error === 'invalid_state'
                      ? 'Your sign-in session expired or did not match. Please try again.'
                      : 'An error occurred during login. Please try again.'}
          </div>
        )}

        {/* Login card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <button
            onClick={handleSlackLogin}
            className="w-full flex items-center justify-center gap-3 bg-[#4A154B] hover:bg-[#3d1140] text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
            </svg>
            Sign in with Slack
          </button>

          <p className="mt-4 text-center text-sm text-gray-500">
            Use your company Slack account to sign in
          </p>
        </div>
      </div>
    </div>
  )
}
